import { DiModuleBuilder } from "@iot-platform/core";
import { GraphDatasource } from "@station/data/datasources/graph.datasource.ts";
import { NotificationAlertDatasource } from "@station/data/datasources/notification-alert.datasource";
import { PestDatasource } from "@station/data/datasources/pest.datasource";
import { RouteDatasource } from "@station/data/datasources/route.datasource";
import { AlertDatasource } from "@station/data/datasources/sensor-alert.datasource";
import { SensorGroupDatasource } from "@station/data/datasources/sensor-group.datasource";
import { SpatialLevelDatasource } from "@station/data/datasources/spatial-level.datasource.ts";
import { StationDatasource } from "@station/data/datasources/station.datasource";
import { ConnectionAlertMapper } from "@station/data/mappers/connection-alert.mapper";
import { GraphMapper } from "@station/data/mappers/graph.mapper.ts";
import { NotificationAlertMapper } from "@station/data/mappers/notification-alert.mapper";
import { PestMapper } from "@station/data/mappers/pest.mapper";
import { PredefinedAlertMapper } from "@station/data/mappers/predefined-sensor-alert.mapper";
import { RouteMapper } from "@station/data/mappers/route.mapper";
import { SensorAlertMapper } from "@station/data/mappers/sensor-alert.mapper";
import { SensorGroupMapper } from "@station/data/mappers/sensor-group.mapper";
import { SpatialLevelResumeMapper } from "@station/data/mappers/spatial-level-resume.mapper";
import { SpatialLevelMapper } from "@station/data/mappers/spatial-level.mapper";
import { StationMapper } from "@station/data/mappers/station.mapper";
import { AlertImplRepository } from "@station/data/repositories/alert.impl-repository";
import { GraphImplRepository } from "@station/data/repositories/graph.impl-repository.ts";
import { NotificationAlertImplRepository } from "@station/data/repositories/notification-alert.impl-repository";
import { PestImplRepository } from "@station/data/repositories/pest.impl-repository";
import { RouteImplRepository } from "@station/data/repositories/route.impl-repository";
import { SensorGroupImplRepository } from "@station/data/repositories/sensor-group.impl-repository";
import { SpatialLevelImplRepository } from "@station/data/repositories/spatial-level.impl-repository.ts";
import { StationImplRepository } from "@station/data/repositories/station.impl-repository";
import type { AlertRepository } from "@station/domain/repositories/alert.repository";
import type { GraphRepository } from "@station/domain/repositories/graph.repository";
import type { NotificationAlertRepository } from "@station/domain/repositories/notification-alert.repository";
import type { PestRepository } from "@station/domain/repositories/pest.repository";
import type { RouteRepository } from "@station/domain/repositories/route.repository";
import type { SensorGroupRepository } from "@station/domain/repositories/sensor-group.repository";
import type { SpatialLevelRepository } from "@station/domain/repositories/spatial-level.repository.ts";
import type { StationRepository } from "@station/domain/repositories/station.repository.ts";
import { ConnectionAlertGetAllUseCase } from "@station/domain/usecases/connection-alert-get-all.usecase";
import { ConnectionAlertUpdateUseCase } from "@station/domain/usecases/connection-alert-update.usecase";
import { CreatedPredefinedAlertGetAllUseCase } from "@station/domain/usecases/created-predefined-alert-get-all.usecase";
import { GraphsGetByUseCase } from "@station/domain/usecases/graph-get-by.usecase.ts";
import { LatestDataGetAllUseCase } from "@station/domain/usecases/latest-data-get-all.usecase";
import { NotificationAlertDeleteUseCase } from "@station/domain/usecases/notification-alert-delete.usecase.ts";
import { NotificationAlertGetAllUseCase } from "@station/domain/usecases/notification-alert-get-all.usecase";
import { NotificationAlertUpdateUseCase } from "@station/domain/usecases/notification-alert-update.usecase";
import { PestGetAllUseCase } from "@station/domain/usecases/pest-get-all.usecase";
import { PredefinedAlertGetAllUseCase } from "@station/domain/usecases/predefined-alert-get-all.usecase";
import { PredefinedAlertUpdateUseCase } from "@station/domain/usecases/predefined-alert-update.usecase";
import { RouteGetAllUseCase } from "@station/domain/usecases/route-get-all.usecase";
import { SensorAlertGetAllUseCase } from "@station/domain/usecases/sensor-alert-get-all.usecase";
import { SensorAlertUpdateUseCase } from "@station/domain/usecases/sensor-alert-update.usecase";
import { SensorGroupGetAllUseCase } from "@station/domain/usecases/sensor-group-get-all.usecase";
import { SpatialLevelGetAllUseCase } from "@station/domain/usecases/spatial-level-get-all.usecase.ts";
import { SpatialLevelGetLocalUseCase } from "@station/domain/usecases/spatial-level-get-local.usecase.ts";
import { SpatialLevelRefreshStationUseCase } from "@station/domain/usecases/spatial-level-refresh-station.usecase.ts";
import { SpatialLevelSetLocalUseCase } from "@station/domain/usecases/spatial-level-set-local.usecase.ts";
import { StationAddSelectedUseCase } from "@station/domain/usecases/station-add-selected.usecase";
import { StationGetByUseCase } from "@station/domain/usecases/station-get-by.usecase.ts";
import { StationGetSelectedUseCase } from "@station/domain/usecases/station-get-selected.usecase";
import { StationRemoveAllSelectedUseCase } from "@station/domain/usecases/station-remove-all-selected.usecase";
import { StationRemoveSelectedUseCase } from "@station/domain/usecases/station-remove-selected.usecase";
import { ConnectionAlertViewmodel } from "@station/presentation/components/detail/alerts/connection-alert/connection-alert.viewmodel.ts";
import { NotificationViewmodel } from "@station/presentation/components/detail/alerts/notification/notification.viewmodel.ts";
import { PestViewmodel } from "@station/presentation/components/detail/alerts/pests/pest.viewmodel.ts";
import { SensorAlertViewmodel } from "@station/presentation/components/detail/alerts/sensor-alert/sensor-alert.viewmodel.ts";
import { StationAlertViewmodel } from "@station/presentation/components/detail/alerts/station-alert.viewmodel.ts";
import { StationDetailHeaderComponentViewmodel } from "@station/presentation/components/detail/header/station-detail-header.component.viewmodel";
import { RelatedChartsViewmodel } from "@station/presentation/components/detail/related-charts/related-charts.viewmodel";
import { RouteViewmodel } from "@station/presentation/components/detail/routes/route.viewmodel";
import { SensorComponentViewmodel } from "@station/presentation/components/detail/sensor-group/components/sensor/sensor.component.viewmodel";
import { SensorGroupComponentViewmodel } from "@station/presentation/components/detail/sensor-group/sensor-group.component.viewmodel.ts";
import { StationDetailViewmodel } from "@station/presentation/components/detail/station-detail.viewmodel";
import { StationListViewmodel } from "@station/presentation/components/list/station-list.viewmodel.ts";
import { StationMapViewmodel } from "@station/presentation/components/map/station-map.viewmodel.ts";
import { StationGraphViewmodel } from "@station/presentation/components/station-graph/station-graph.viewmodel.ts";
import { StationLayoutMobileViewModel } from "@station/presentation/layout/station-module-mobile-layout.viewmodel.tsx";
import { StationCountHeaderViewmodel } from "@station/presentation/pages/station/mobile/components/station-count-header.viewmodel.ts";
import { StationDetailMobileViewmodel } from "@station/presentation/pages/station/mobile/detail/station-detail-mobile.viewmodel.ts";
import { StationListMobileViewmodel } from "@station/presentation/pages/station/mobile/list/station-list-mobile.viewmodel.ts";
import { StationMapMobileViewmodel } from "@station/presentation/pages/station/mobile/map/station-map-mobile.viewmodel.ts";
import { StationSearchMobileViewmodel } from "@station/presentation/pages/station/mobile/search/station-search-mobile.viewmodel.ts";
import { StationSelectedMobileViewmodel } from "@station/presentation/pages/station/mobile/selected/station-selected-mobile.viewmodel.ts";
import { StationResponsivePageViewmodel } from "@station/presentation/pages/station/station-responsive-page.viewmodel.ts";
import { StationWebPageViewmodel } from "@station/presentation/pages/station/web/station-web-page.viewmodel.ts";
import { stationsTypes } from "@station/station-types.di.ts";
import { ContainerModule } from "inversify";

const webStationModule = new DiModuleBuilder();

webStationModule.registerSubModules(() => ({
    datasources: new ContainerModule(({ bind }) => {
        bind(AlertDatasource).toSelf();
        bind(GraphDatasource).toSelf();
        bind(NotificationAlertDatasource).toSelf();
        bind(PestDatasource).toSelf();
        bind(RouteDatasource).toSelf();
        bind(SensorGroupDatasource).toSelf();
        bind(SpatialLevelDatasource).toSelf();
        bind(StationDatasource).toSelf();
    }),
    mappers: new ContainerModule(({ bind }) => {
        bind(ConnectionAlertMapper).toSelf();
        bind(GraphMapper).toSelf();
        bind(NotificationAlertMapper).toSelf();
        bind(PestMapper).toSelf();
        bind(PredefinedAlertMapper).toSelf();
        bind(RouteMapper).toSelf();
        bind(SensorAlertMapper).toSelf();
        bind(SensorGroupMapper).toSelf();
        bind(SpatialLevelMapper).toSelf();
        bind(SpatialLevelResumeMapper).toSelf();
        bind(StationMapper).toSelf();
    }),
    repositories: new ContainerModule(({ bind }) => {
        bind<AlertRepository>(stationsTypes.repositories.AlertRepository).to(
            AlertImplRepository,
        );
        bind<GraphRepository>(stationsTypes.repositories.GraphRepository).to(
            GraphImplRepository,
        );
        bind<NotificationAlertRepository>(
            stationsTypes.repositories.NotificationAlertRepository,
        ).to(NotificationAlertImplRepository);
        bind<PestRepository>(stationsTypes.repositories.PestRepository).to(
            PestImplRepository,
        );
        bind<RouteRepository>(stationsTypes.repositories.RouteRepository).to(
            RouteImplRepository,
        );
        bind<SpatialLevelRepository>(
            stationsTypes.repositories.SpatialLevelRepository,
        )
            .to(SpatialLevelImplRepository)
            .inSingletonScope();
        bind<StationRepository>(stationsTypes.repositories.StationRepository)
            .to(StationImplRepository)
            .inSingletonScope();
        bind<SensorGroupRepository>(
            stationsTypes.repositories.SensorGroupRepository,
        ).to(SensorGroupImplRepository);
    }),
    useCases: new ContainerModule(({ bind }) => {
        bind(ConnectionAlertGetAllUseCase).toSelf();
        bind(ConnectionAlertUpdateUseCase).toSelf();
        bind(CreatedPredefinedAlertGetAllUseCase).toSelf();
        bind(GraphsGetByUseCase).toSelf();
        bind(LatestDataGetAllUseCase).toSelf();
        bind(NotificationAlertDeleteUseCase).toSelf();
        bind(NotificationAlertGetAllUseCase).toSelf();
        bind(NotificationAlertUpdateUseCase).toSelf();
        bind(PestGetAllUseCase).toSelf();
        bind(PredefinedAlertGetAllUseCase).toSelf();
        bind(PredefinedAlertUpdateUseCase).toSelf();
        bind(RouteGetAllUseCase).toSelf();
        bind(SensorAlertGetAllUseCase).toSelf();
        bind(SensorAlertUpdateUseCase).toSelf();
        bind(SensorGroupGetAllUseCase).toSelf();
        bind(SpatialLevelGetAllUseCase).toSelf();
        bind(SpatialLevelGetLocalUseCase).toSelf();
        bind(SpatialLevelRefreshStationUseCase).toSelf();
        bind(SpatialLevelSetLocalUseCase).toSelf();
        bind(StationAddSelectedUseCase).toSelf();
        bind(StationGetByUseCase).toSelf();
        bind(StationGetSelectedUseCase).toSelf();
        bind(StationRemoveAllSelectedUseCase).toSelf();
        bind(StationRemoveSelectedUseCase).toSelf();
    }),
    viewModels: new ContainerModule(({ bind }) => {
        bind(ConnectionAlertViewmodel).toSelf();
        bind(NotificationViewmodel).toSelf();
        bind(PestViewmodel).toSelf();
        bind(RelatedChartsViewmodel).toSelf();
        bind(RouteViewmodel).toSelf();
        bind(SensorAlertViewmodel).toSelf();
        bind(SensorComponentViewmodel).toSelf();
        bind(SensorGroupComponentViewmodel).toSelf();
        bind(StationAlertViewmodel).toSelf();
        bind(StationCountHeaderViewmodel).toSelf();
        bind(StationDetailHeaderComponentViewmodel).toSelf();
        bind(StationDetailMobileViewmodel).toSelf();
        bind(StationDetailViewmodel).toSelf();
        bind(StationGraphViewmodel).toSelf();
        bind(StationLayoutMobileViewModel).toSelf();
        bind(StationListMobileViewmodel).toSelf();
        bind(StationListViewmodel).toSelf();
        bind(StationMapMobileViewmodel).toSelf();
        bind(StationMapViewmodel).toSelf();
        bind(StationResponsivePageViewmodel).toSelf();
        bind(StationSearchMobileViewmodel).toSelf();
        bind(StationSelectedMobileViewmodel).toSelf();
        bind(StationWebPageViewmodel).toSelf();
    }),
}));

webStationModule.registerModule();

export { webStationModule };
